// ── company-form-modal: the create/edit company Modal (POST/PATCH companies) ─
// Wave 1 (Companies). One @vjal/ui Modal that backs both the create flow (POST
// /companies) and the edit flow (PATCH /companies/:id) — the `mode` prop only
// changes the title, the submit label, and the initial values. Presentational +
// validation-only; the server is authoritative (staff/super-admin only for
// mutations — the server re-checks and 403s, which we render honestly inline).
//
// Honest write contract: a server rejection (validation / permission-denied /
// gate-closed / unavailable) is surfaced INLINE via describeCompanyWriteError —
// never swallowed, never faked. The caller (the page) owns the actual mutation
// and closes + toasts ONLY after the backend confirms success.

import { useEffect, useId, useRef, useState } from "react";
import { Alert, Button, Input, Modal, Select } from "@vjal/ui";

import type { CoachingCompanyDto } from "../../services/coaching-dtos";
import { COMPANY_STATUSES, describeCompanyWriteError, type CompanyStatus } from "./companies-write";

export interface CompanyFormValues {
  name: string;
  status: CompanyStatus;
}

export interface CompanyFormModalProps {
  open: boolean;
  mode: "create" | "edit";
  /** The company being edited (edit mode only) — seeds the initial values. */
  company?: CoachingCompanyDto | null;
  onClose: () => void;
  /** The actual write — owned by the page (react-query mutation). */
  onSubmit: (values: CompanyFormValues) => Promise<unknown>;
  submitting?: boolean;
  submitError?: unknown;
}

interface FieldErrors {
  name?: string;
}

function initialValues(mode: "create" | "edit", company?: CoachingCompanyDto | null): CompanyFormValues {
  if (mode === "edit" && company) {
    const status = company.status === "archived" ? "archived" : "active";
    return { name: company.name ?? "", status };
  }
  return { name: "", status: "active" };
}

export function CompanyFormModal({
  open,
  mode,
  company,
  onClose,
  onSubmit,
  submitting = false,
  submitError
}: CompanyFormModalProps) {
  const [values, setValues] = useState<CompanyFormValues>(() => initialValues(mode, company));
  const [errors, setErrors] = useState<FieldErrors>({});
  const formId = useId();

  // Seed ONLY on the open false->true transition (and when the target company
  // identity changes), never on every parent re-render — otherwise an in-progress
  // edit would be clobbered by a background re-render.
  const wasOpen = useRef(false);
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    const companyKey = company?.id ?? null;
    if (open && (!wasOpen.current || seededFor.current !== companyKey)) {
      setValues(initialValues(mode, company));
      setErrors({});
      seededFor.current = companyKey;
    }
    wasOpen.current = open;
  }, [open, mode, company]);

  const set = <K extends keyof CompanyFormValues>(key: K, value: CompanyFormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (!values.name.trim()) next.name = "Company name is required.";
    return next;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const localErrors = validate();
    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors);
      return;
    }
    setErrors({});
    try {
      await onSubmit({ name: values.name.trim(), status: values.status });
      // The page closes the modal + toasts on success; nothing else to do here.
    } catch {
      // The owning mutation surfaces the error via `submitError`; swallow the
      // re-throw so the modal stays open with the inline error shown.
    }
  }

  const topError = describeCompanyWriteError(submitError);

  return (
    <Modal open={open} onClose={onClose} title={mode === "create" ? "Add company" : "Edit company"}>
      <form id={formId} onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-4" noValidate>
        {topError && <Alert variant={topError.tone === "warning" ? "warning" : "error"}>{topError.message}</Alert>}

        <Input
          label="Company name"
          value={values.name}
          onChange={(event) => set("name", event.target.value)}
          error={errors.name}
          autoComplete="organization"
          required
        />

        {mode === "edit" && (
          <Select
            label="Lifecycle status"
            value={values.status}
            onChange={(event) => set("status", event.target.value as CompanyStatus)}
            hint="Set to Archived to remove the company from active navigation. Use the Archive action for a confirmation step."
          >
            {COMPANY_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status === "active" ? "Active" : "Archived"}
              </option>
            ))}
          </Select>
        )}

        <div className="mt-2 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={submitting}>
            {mode === "create" ? "Create company" : "Save changes"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
